/**
 * Document Generator Workspace
 * Main workspace for document generation with template selection, form filling, and editing
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Plus,
  List,
  Clock,
  CheckSquare,
  ArrowLeft,
  Loader
} from 'lucide-react';
import { GlobalNavigation } from '../../common/GlobalNavigation';
import { Card } from '../../common/Card';
import { Button } from '../../common/Button';
import { TemplateList } from './TemplateList';
import { DocumentForm } from './DocumentForm';
import { DocumentList } from './DocumentList';
import { PendingApprovals } from './PendingApprovals';
import { DocumentEditor } from './DocumentEditor';
import { useAuth } from '../../../contexts/AuthContext';
import { documentService } from '../../../services/api/documentGeneratorService';
import {
  DocumentTemplate,
  GeneratedDocument
} from '../../../types/documentGenerator.types';

type WorkspaceView = 'templates' | 'create' | 'edit' | 'documents' | 'approvals';

interface Tab {
  id: WorkspaceView;
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  { id: 'templates', label: 'New Document', icon: <Plus className="w-4 h-4" /> },
  { id: 'documents', label: 'My Documents', icon: <List className="w-4 h-4" /> },
  { id: 'approvals', label: 'Pending Approvals', icon: <CheckSquare className="w-4 h-4" /> }
];

export const DocumentGeneratorWorkspace: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState<WorkspaceView>('templates');
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);
  const [currentDocument, setCurrentDocument] = useState<GeneratedDocument | null>(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handler for template selection
  const handleTemplateSelect = useCallback((template: DocumentTemplate) => {
    setSelectedTemplate(template);
    setActiveTab('create');
  }, []);

  // Handler for document generation success
  const handleDocumentGenerated = useCallback(async (documentId: string) => {
    if (!token) return;

    setLoadingDocument(true);
    try {
      const doc = await documentService.getById(token, documentId);
      setCurrentDocument(doc);
      setSelectedTemplate(null);
      setActiveTab('edit');
    } catch (err: any) {
      setError(err.message || 'Failed to load generated document');
      setActiveTab('documents');
    } finally {
      setLoadingDocument(false);
    }
  }, [token]);

  // Handler for viewing/editing an existing document
  const handleViewDocument = useCallback(async (document: GeneratedDocument) => {
    if (!token) return;

    setLoadingDocument(true);
    try {
      // Fetch full document details
      const doc = await documentService.getById(token, document._id);
      setCurrentDocument(doc);
      setActiveTab('edit');
    } catch (err: any) {
      setError(err.message || 'Failed to load document');
    } finally {
      setLoadingDocument(false);
    }
  }, [token]);

  // Handler for editing document
  const handleEditDocument = useCallback(async (document: GeneratedDocument) => {
    if (!token) return;

    setLoadingDocument(true);
    try {
      const doc = await documentService.getById(token, document._id);
      setCurrentDocument(doc);
      setActiveTab('edit');
    } catch (err: any) {
      setError(err.message || 'Failed to load document');
    } finally {
      setLoadingDocument(false);
    }
  }, [token]);

  // Handler for going back from create/edit views
  const handleBack = useCallback(() => {
    setSelectedTemplate(null);
    setCurrentDocument(null);
    setActiveTab('templates');
  }, []);

  // Handler for canceling form
  const handleCancelForm = useCallback(() => {
    setSelectedTemplate(null);
    setActiveTab('templates');
  }, []);

  // Handler for document update
  const handleDocumentUpdate = useCallback((updatedDocument: GeneratedDocument) => {
    setCurrentDocument(updatedDocument);
  }, []);

  // Handler for closing editor
  const handleCloseEditor = useCallback(() => {
    setCurrentDocument(null);
    setActiveTab('documents');
  }, []);

  // Get breadcrumbs based on current view
  const getBreadcrumbs = () => {
    const base = [
      { label: 'Home', path: '/' },
      { label: 'Document Generator', path: '/document-generator' }
    ];

    switch (activeTab) {
      case 'create':
        return [...base, { label: 'New Document' }];
      case 'edit':
        return [...base, { label: currentDocument?.title || 'Edit Document' }];
      case 'documents':
        return [...base, { label: 'My Documents' }];
      case 'approvals':
        return [...base, { label: 'Pending Approvals' }];
      default:
        return base;
    }
  };

  // Render main content based on active view
  const renderContent = () => {
    // Loading state
    if (loadingDocument) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Loader className="w-10 h-10 animate-spin text-primary-600 mx-auto mb-4" />
            <p className="text-text-secondary">Loading document...</p>
          </div>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <Card className="text-center py-12">
          <div className="text-red-500 mb-4">
            <FileText className="w-12 h-12 mx-auto mb-2" />
            <p>{error}</p>
          </div>
          <Button variant="secondary" onClick={() => setError(null)}>
            Try Again
          </Button>
        </Card>
      );
    }

    switch (activeTab) {
      case 'templates':
        return (
          <TemplateList
            onSelectTemplate={handleTemplateSelect}
            selectionMode={true}
          />
        );

      case 'create':
        if (!selectedTemplate) {
          return (
            <Card className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-text-tertiary mb-4" />
              <p className="text-text-secondary">No template selected</p>
              <Button
                variant="secondary"
                onClick={handleBack}
                className="mt-4"
              >
                Select a Template
              </Button>
            </Card>
          );
        }
        return (
          <div>
            <Button
              variant="secondary"
              onClick={handleBack}
              className="mb-6 gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Templates
            </Button>
            <DocumentForm
              template={selectedTemplate}
              onSuccess={handleDocumentGenerated}
              onCancel={handleCancelForm}
            />
          </div>
        );

      case 'edit':
        if (!currentDocument) {
          return (
            <Card className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-text-tertiary mb-4" />
              <p className="text-text-secondary">No document selected</p>
              <Button
                variant="secondary"
                onClick={() => setActiveTab('documents')}
                className="mt-4"
              >
                View Documents
              </Button>
            </Card>
          );
        }
        return (
          <DocumentEditor
            document={currentDocument}
            onUpdate={handleDocumentUpdate}
            onClose={handleCloseEditor}
          />
        );

      case 'documents':
        return (
          <DocumentList
            onViewDocument={handleViewDocument}
            onEditDocument={handleEditDocument}
          />
        );

      case 'approvals':
        return (
          <PendingApprovals
            onViewDocument={handleViewDocument}
          />
        );

      default:
        return null;
    }
  };

  // Determine if we should show the tab bar
  const showTabBar = activeTab === 'templates' || activeTab === 'documents' || activeTab === 'approvals';

  return (
    <div className="min-h-screen bg-bg-primary">
      <GlobalNavigation
        title="Document Generator"
        breadcrumbs={getBreadcrumbs()}
        showBackButton={true}
        onBack={() => navigate('/')}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation - Only show for main views */}
        {showTabBar && (
          <div className="mb-8">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${activeTab === tab.id
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-text-secondary hover:text-text-primary hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Main Content */}
        {renderContent()}
      </main>
    </div>
  );
};

export default DocumentGeneratorWorkspace;
